import type { Comment, Exchange, Persisted } from '@tunasong/schemas'
import { getExchange, isAgentAuthoredComment } from '@tunasong/schemas'
import { isPendingProposal, isProposal, type ProposalComment } from './proposal-resolve.js'

/**
 * One agent exchange (Option A, docs/ux/2026-10-24-agent-anchored-threads.md §2, §8): a question about one element,
 * its answers (Try again adds siblings) and any proposals in them, grouped by `metadata.exchange.id`.
 *
 * What a viewer receives is what the server's ACLs allow (§9): the asker gets everything; another editor gets only
 * the proposals. So an exchange seen by another editor has no question and no replies.
 */
export interface AnchoredExchange {
  id: string
  agentId: string
  anchor?: Exchange['anchor']
  askedBy?: string
  /** A proactive review (GH #3099): nobody asked, so no question and no anchor; the thread heads it with T21 */
  isReview: boolean
  /** The asker's question comment; absent for another editor, and for exchanges from before questions were saved */
  question?: Persisted<Comment>
  /** The agent's answers, oldest first */
  replies: Persisted<Comment>[]
  proposals: ProposalComment[]
  pendingProposals: ProposalComment[]
  /** The asker discarded it (stamped on the question) */
  discarded: boolean
  /** The newest comment's creation time, for newest-first ordering */
  lastAt: string
}

const byCreatedAt = (a: Persisted<Comment>, b: Persisted<Comment>) => a.createdAt.localeCompare(b.createdAt)

/** Group an agent's comments into its exchanges. Comments without an exchange (plain chat, nudges) are left out. */
export const groupExchanges = (agentId: string, comments: Persisted<Comment>[]): AnchoredExchange[] => {
  const groups = new Map<string, { exchange: Exchange; comments: Persisted<Comment>[] }>()
  for (const comment of comments) {
    const exchange = getExchange(comment)
    if (!exchange) {
      continue
    }
    const group = groups.get(exchange.id)
    if (group) {
      group.comments.push(comment)
      // Prefer the richest copy: the question carries the discard stamp
      if (!isAgentAuthoredComment(comment)) {
        group.exchange = { ...group.exchange, ...exchange }
      }
    } else {
      groups.set(exchange.id, { exchange, comments: [comment] })
    }
  }
  return [...groups.values()].map(({ exchange, comments: members }) => {
    const sorted = [...members].sort(byCreatedAt)
    const proposals = sorted.filter(isProposal)
    const question = sorted.find(comment => !isAgentAuthoredComment(comment))
    const questionExchange = question ? getExchange(question) : undefined
    return {
      id: exchange.id,
      agentId,
      anchor: exchange.anchor,
      askedBy: exchange.askedBy,
      isReview: exchange.kind === 'review',
      question,
      replies: sorted.filter(comment => isAgentAuthoredComment(comment) && !isProposal(comment)),
      proposals,
      pendingProposals: proposals.filter(isPendingProposal),
      discarded: Boolean(questionExchange?.discardedAt),
      lastAt: sorted[sorted.length - 1]?.createdAt ?? '',
    }
  })
}

/**
 * Open (§2): not discarded, and holding an answer or an undecided proposal. A question still waiting for its answer
 * is not open yet; the inline surface shows it meanwhile.
 */
export const isOpenExchange = (exchange: AnchoredExchange): boolean =>
  !exchange.discarded && (exchange.replies.length > 0 || exchange.pendingProposals.length > 0)

export interface BlockScope {
  /** The block's own id and its descendants' (an inline chord sits inside a line) */
  blockIds: string[]
  /** Every element id in the document */
  docIds: ReadonlySet<string>
}

/** Whether an anchor shows on this block: on its element's block, or on its container once the element is gone */
export const isAnchoredOnBlock = (anchor: AnchoredExchange['anchor'], { blockIds, docIds }: BlockScope): boolean => {
  if (!anchor) {
    return false
  }
  const own = new Set(blockIds)
  if (docIds.has(anchor.elementId)) {
    return own.has(anchor.elementId)
  }
  return Boolean(anchor.containerId) && own.has(anchor.containerId ?? '')
}

/**
 * The open exchanges a block's marker shows, newest first. An exchange shows on the block holding its element; once
 * the element is gone from the document, on its container (§12). With both gone it shows on no block and lives in
 * the chat only.
 *
 * `blockIds` are the block's own id and its descendants' (an inline chord sits inside a line); `docIds` are every
 * element id in the document.
 */
export const exchangesForBlock = (exchanges: AnchoredExchange[], scope: BlockScope): AnchoredExchange[] =>
  exchanges
    .filter(isOpenExchange)
    .filter(({ anchor }) => isAnchoredOnBlock(anchor, scope))
    .sort((a, b) => b.lastAt.localeCompare(a.lastAt))
